<template>
    <BaseTemplate app-name="Sistema RIS" page-title="">
        <template #content>
            <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
                <!-- Header com seleção de sala -->
                <div v-if="!currentConsultation" class="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-4">
                    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div class="flex flex-col md:flex-row md:items-center gap-3 w-full">
                            <!-- Agenda primeiro -->
                            <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full md:w-auto">
                                <label class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Agenda:</label>
                                <select
                                    v-model="selectedSchedule"
                                    :disabled="isInService"
                                    class="w-full sm:w-56 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">Selecione uma agenda</option>
                                    <option v-for="schedule in schedules" :key="schedule.id" :value="schedule.id">{{ schedule.name }}</option>
                                </select>
                            </div>
                            <!-- Sala depois -->
                            <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 w-full md:w-auto">
                                <label class="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Sala de Atendimento:</label>
                                <select
                                    v-model="selectedRoom"
                                    :disabled="isInService || !selectedSchedule"
                                    class="w-full sm:w-56 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">{{ selectedSchedule ? 'Selecione uma sala' : 'Selecione uma agenda primeiro' }}</option>
                                    <option v-for="room in rooms" :key="room.id" :value="room.id">{{ room.name }}</option>
                                </select>
                                <div v-if="selectedSchedule && rooms.length === 0" class="text-xs text-orange-600 dark:text-orange-400 mt-1 sm:mt-0">
                                    Nenhuma sala vinculada a esta agenda
                                </div>
                            </div>
                            <div v-if="selectedRoom" class="flex items-center gap-2 mt-2 md:mt-0 md:ml-2">
                                <div class="w-3 h-3 rounded-full" :class="getRoomStatusColor()"></div>
                                <span class="text-sm text-gray-600 dark:text-gray-400">{{ getRoomStatus() }}</span>
                            </div>
                        </div>

                        <!-- Status do atendimento atual -->
                        <div v-if="currentConsultation" class="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg mt-3 md:mt-0">
                            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                            <span class="text-sm font-medium text-blue-800 dark:text-blue-200">Em atendimento</span>
                        </div>
                    </div>
                </div>

                <!-- Layout quando NÃO há atendimento em andamento -->
                <div v-if="!isInService" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Lista de Pacientes Aguardando -->
                    <div class="lg:col-span-2">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Fila de Atendimento</h3>
                                <p class="text-sm text-gray-600 dark:text-gray-400">Pacientes agendados para hoje</p>
                            </div>
                            
                            <div class="max-h-96 overflow-y-auto">
                                <div v-if="isLoadingConsultations" class="text-center py-8">
                                    <div class="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                                    <p class="mt-2 text-gray-600 dark:text-gray-400">Carregando consultas...</p>
                                </div>
                                
                                <div v-else-if="consultations.length === 0" class="text-center py-8">
                                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <h3 class="mt-2 text-lg font-medium text-gray-900 dark:text-white">
                                        {{ selectedSchedule && rooms.length === 0 ? 'Nenhuma sala vinculada' : 'Nenhuma consulta na fila' }}
                                    </h3>
                                    <p class="text-gray-500 dark:text-gray-400">
                                        {{ selectedSchedule && rooms.length === 0 
                                            ? 'Esta agenda não possui salas vinculadas. Configure as salas no cadastro da agenda.' 
                                            : 'Selecione uma sala e agenda para visualizar as consultas' }}
                                    </p>
                                </div>
                                
                                <div v-else>
                                    <div v-for="(consultation) in consultations" :key="consultation.id" 
                                         :class="['p-4 border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer', 
                                                  selectedConsultation?.id === consultation.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' : '']"
                                         @click="selectConsultation(consultation)">
                                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                                            <div class="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                                                <div class="flex-shrink-0">
                                                    <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                        <span class="text-sm font-medium text-blue-800 dark:text-blue-200">{{ consultation.sequence_number }}</span>
                                                    </div>
                                                </div>
                                                <div class="min-w-0">
                                                    <h4 class="font-medium text-gray-900 dark:text-white truncate">{{ consultation.patient_name }}</h4>
                                                    <p class="text-sm text-gray-600 dark:text-gray-400 truncate">{{ consultation.appointment?.service?.descricao || 'Serviço não definido' }}</p>
                                                    <p class="text-xs text-gray-500 dark:text-gray-400">
                                                        Confirmado em: {{ formatDateTime(consultation.appointment?.confirmed || consultation.created_at) }} - Seq: {{ consultation.sequence_number }}
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end mt-3 sm:mt-0">
                                                <span :class="['px-2 py-1 rounded text-xs font-medium', getStatusClass(consultation.status)]">
                                                    {{ getStatusLabel(consultation.status) }}
                                                </span>
                                                <!-- Botão Chamar -->
                                                <button v-if="consultation.status === 'waiting'" 
                                                        @click.stop="callPatient(consultation, 'first')"
                                                        class="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs font-medium transition-colors">
                                                    Chamar
                                                </button>
                                                <!-- Botões para status 'Chamado' -->
                                                <div v-if="consultation.status === 'called'" class="flex flex-wrap gap-2">
                                                    <button @click.stop="callPatient(consultation, 'recall')"
                                                            class="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs font-medium transition-colors">
                                                        Chamar Novamente
                                                    </button>
                                                    <button @click.stop="startService(consultation)"
                                                            class="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium transition-colors">
                                                        Atender
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Painel de Atendimento (Lateral) -->
                    <div class="lg:col-span-1">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Painel de Atendimento</h3>
                            </div>
                            
                            <div class="p-4 space-y-4">
                                <div v-if="!currentConsultation" class="text-center py-8">
                                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <p class="mt-2 text-gray-600 dark:text-gray-400">Selecione uma consulta para iniciar o atendimento</p>
                                </div>
                                
                                <div v-else class="space-y-4">
                                    <!-- Informações do Paciente -->
                                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <h4 class="font-medium text-gray-900 dark:text-white mb-2">Paciente em Atendimento</h4>
                                        <div class="space-y-2 text-sm text-gray-900 dark:text-white">
                                            <div><span class="font-medium">Nome:</span> {{ currentConsultation.patient_name }}</div>
                                            <div><span class="font-medium">Serviço:</span> {{ currentConsultation.appointment?.service?.descricao || 'Não definido' }}</div>
                                            <div><span class="font-medium">Confirmado em:</span> {{ formatDateTime(currentConsultation.appointment?.confirmed || currentConsultation.created_at) }}</div>
                                            <div><span class="font-medium">Sequencial:</span> {{ currentConsultation.sequence_number }}</div>
                                           
                                            <div v-if="currentConsultation.start_time"><span class="font-medium">Início do Atendimento:</span> {{ formatDateTime(currentConsultation.start_time) }}</div>
                                        </div>
                                    </div>

                                    <!-- Cronômetro -->
                                    <div class="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                        <h5 class="font-medium text-blue-800 dark:text-blue-200 mb-2">Tempo de Atendimento</h5>
                                        <div class="text-3xl font-mono font-bold text-blue-600 dark:text-blue-300">
                                            {{ formatTime(serviceTime) }}
                                        </div>
                                    </div>

                                    <!-- Materiais Utilizados -->
                                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <h5 class="font-medium text-gray-900 dark:text-white mb-3">Materiais Utilizados</h5>
                                        <MateriaisUtilizados 
                                            v-if="currentConsultation.appointment"
                                            :schedule-id="selectedSchedule"
                                            :appointment-id="currentConsultation.appointment.id"
                                            @material-adicionado="onMaterialAdicionado"
                                            @material-removido="onMaterialRemovido"
                                            @material-atualizado="onMaterialAtualizado"
                                        />
                                        <div v-else class="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                                            Informações do agendamento não disponíveis
                                        </div>
                                    </div>

                                    <!-- Controles de Atendimento -->
                                    <div class="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <!-- Botões de Documentos Médicos -->
                                        <div class="space-y-2">
                                            <!-- Botão Laudar -->
                                            <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                    @click="openLaudoModal"
                                                    :disabled="isLoading"
                                                    class="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                </svg>
                                                <span>Laudar</span>
                                            </button>
                                            
                                            <!-- Botão Atestado -->
                                            <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                    @click="openAtestadoModal"
                                                    :disabled="isLoading"
                                                    class="w-full px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                </svg>
                                                <span>Atestado</span>
                                            </button>
                                            
                                            <!-- Botão Prescrever -->
                                            <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                    @click="openPrescricaoModal"
                                                    :disabled="isLoading"
                                                    class="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                                                </svg>
                                                <span>Prescrição</span>
                                            </button>
                                        </div>
                                        
                                        <button v-if="currentConsultation.status === 'in_progress'" 
                                                @click="finishService"
                                                :disabled="isLoading"
                                                class="w-full px-4 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors">
                                            <span v-if="isLoading">Finalizando...</span>
                                            <span v-else>Finalizar Atendimento</span>
                                        </button>
                                    </div>
                                    
                                    <!-- Observações -->
                                    <div class="space-y-2">
                                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Observações:</label>
                                        <textarea v-model="consultationNotes" 
                                                  placeholder="Adicione observações sobre o atendimento..."
                                                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                  rows="3"></textarea>
                                        <button @click="saveNotes" 
                                                :disabled="isLoading || !consultationNotes.trim()"
                                                class="w-full px-3 py-2 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded text-sm transition-colors">
                                            Salvar Observações
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Layout quando há atendimento em andamento -->
                <div v-if="isInService" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Painel Principal de Atendimento -->
                    <div class="lg:col-span-1">
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Atendimento em Andamento</h3>
                                <p class="text-sm text-gray-600 dark:text-gray-400">{{ currentConsultation?.patient_name }}</p>
                            </div>
                            
                            <div class="p-6 space-y-6">
                                <!-- Informações do Paciente -->
                                <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                    <h4 class="font-medium text-blue-800 dark:text-blue-200 mb-3">Informações do Paciente</h4>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span class="font-medium text-blue-700 dark:text-blue-300">Nome:</span>
                                            <p class="text-blue-800 dark:text-blue-200">{{ currentConsultation.patient_name }}</p>
                                        </div>
                                        <div>
                                            <span class="font-medium text-blue-700 dark:text-blue-300">Serviço:</span>
                                            <p class="text-blue-800 dark:text-blue-200">{{ currentConsultation.appointment?.service?.descricao || 'Não definido' }}</p>
                                        </div>
                                        <div><span class="font-medium text-blue-700 dark:text-blue-300">Confirmado em:</span>
                                            <p class="text-blue-800 dark:text-blue-200">{{ formatDateTime(currentConsultation.appointment?.confirmed || currentConsultation.created_at) }}</p>
                                        </div>
                                        <div>
                                            <span class="font-medium text-blue-700 dark:text-blue-300">Sequencial:</span>
                                            <p class="text-blue-800 dark:text-blue-200">{{ currentConsultation.sequence_number }}</p>
                                        </div>
                
                                        <div v-if="currentConsultation.start_time" class="md:col-span-2">
                                            <span class="font-medium text-blue-700 dark:text-blue-300">Início do Atendimento:</span>
                                            <p class="text-blue-800 dark:text-blue-200">{{ formatDateTime(currentConsultation.start_time) }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Cronômetro -->
                                <div class="text-center bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6">
                                    <h5 class="font-medium text-blue-800 dark:text-blue-200 mb-3">Tempo de Atendimento</h5>
                                    <div class="text-4xl font-mono font-bold text-blue-600 dark:text-blue-300">
                                        {{ formatTime(serviceTime) }}
                                    </div>
                                </div>

                                <!-- Controles de Atendimento -->
                                <div class="space-y-4">
                                    <!-- Botões de Documentos Médicos -->
                                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <!-- Botão Laudar -->
                                        <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                @click="openLaudoModal"
                                                :disabled="isLoading"
                                                class="px-4 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                            </svg>
                                            <span>Laudar</span>
                                        </button>
                                        
                                        <!-- Botão Atestado -->
                                        <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                @click="openAtestadoModal"
                                                :disabled="isLoading"
                                                class="px-4 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                            </svg>
                                            <span>Atestado</span>
                                        </button>
                                        
                                        <!-- Botão Prescrever -->
                                        <button v-if="currentConsultation.status === 'in_progress' && isMedico" 
                                                @click="openPrescricaoModal"
                                                :disabled="isLoading"
                                                class="px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                                            </svg>
                                            <span>Prescrição</span>
                                        </button>
                                    </div>
                                    
                                    <button v-if="currentConsultation.status === 'in_progress'" 
                                            @click="finishService"
                                            :disabled="isLoading"
                                            class="w-full px-6 py-4 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium text-lg transition-colors">
                                        <span v-if="isLoading">Finalizando...</span>
                                        <span v-else>Finalizar Atendimento</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Painel de Materiais Utilizados -->
                    <div class="lg:col-span-1">
                        <MateriaisUtilizados 
                            v-if="currentConsultation.appointment"
                            :schedule-id="selectedSchedule"
                            :appointment-id="currentConsultation.appointment.id"
                            :consultation-notes="consultationNotes"
                            :is-loading="isLoading"
                            @material-adicionado="onMaterialAdicionado"
                            @material-removido="onMaterialRemovido"
                            @material-atualizado="onMaterialAtualizado"
                            @update-notes="updateConsultationNotes"
                            @save-notes="saveNotes"
                        />
                        <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <div class="text-center py-8">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <p class="mt-2 text-gray-600 dark:text-gray-400">Informações do agendamento não disponíveis</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Modal de Confirmação de Finalização -->
                <div v-if="showFinishModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" @click.stop>
                        <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 dark:bg-green-900 rounded-full mb-4">
                            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">Finalizar Atendimento</h3>
                        <p class="text-gray-600 dark:text-gray-400 text-center mb-6">
                            Tem certeza de que deseja finalizar o atendimento de <strong>{{ currentConsultation?.patient_name }}</strong>?
                        </p>
                        <div class="flex gap-3">
                            <button @click="showFinishModal = false" 
                                    class="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                Cancelar
                            </button>
                            <button @click="confirmFinishService" 
                                    :disabled="isLoading"
                                    class="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded-lg transition-colors">
                                <span v-if="isLoading">Finalizando...</span>
                                <span v-else>Confirmar</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Aviso de Sessão Bloqueada -->
                <div v-if="isSessionLocked" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div class="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 text-center">
                        <div class="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Atendimento em Andamento</h2>
                        <p class="text-gray-600 dark:text-gray-400 mb-4">
                            Você possui um atendimento em andamento que deve ser finalizado antes de continuar.
                        </p>
                        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Paciente: <strong>{{ currentConsultation?.patient_name }}</strong><br>
                            Tempo: {{ formatTime(serviceTime) }}
                        </p>
                        <button @click="returnToService" 
                                class="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                            Retornar ao Atendimento
                        </button>
                    </div>
                </div>

                <!-- Modal de Laudo -->
                <LaudoModalAtendimento
                    v-if="showLaudoModal"
                    :is-visible="showLaudoModal"
                    :paciente="laudoPaciente"
                    :modo-popup="true"
                    @close="closeLaudoModal"
                    @laudo-salvo="onLaudoSalvo"
                    @laudo-finalizado="onLaudoFinalizado"
                />

                <!-- Modal de Atestado -->
                <AtestadoModalAtendimento
                    v-if="showAtestadoModal"
                    :is-visible="showAtestadoModal"
                    :paciente="atestadoPaciente"
                    @close="closeAtestadoModal"
                    @atestado-emitido="onAtestadoEmitido"
                    @atestado-salvo="onAtestadoSalvo"
                />

                <!-- Modal de Prescrição -->
                <PrescricaoModalAtendimento
                    v-if="showPrescricaoModal"
                    :is-visible="showPrescricaoModal"
                    :paciente="prescricaoPaciente"
                    @close="closePrescricaoModal"
                    @prescricao-emitida="onPrescricaoEmitida"
                    @prescricao-salva="onPrescricaoSalva"
                />

                <!-- Banner Message -->
                <BannerMessage
                    v-if="banner.show"
                    :type="banner.type"
                    :message="banner.message"
                    @close="banner.show = false"
                />
            </div>
        </template>
    </BaseTemplate>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import BannerMessage from '@/components/BannerMessage.vue'
import MateriaisUtilizados from '@/components/Atendimento/MateriaisUtilizados.vue'
import LaudoModalAtendimento from './components/LaudoModalAtendimento.vue'
import AtestadoModalAtendimento from './components/AtestadoModalAtendimento.vue'
import PrescricaoModalAtendimento from './components/PrescricaoModalAtendimento.vue'
import { useUserStore } from '@/stores/userStore'
import apiClient from '@/services/authService';

// Estados principais
const selectedRoom = ref('')
const selectedSchedule = ref('')
const selectedConsultation = ref(null)
const currentConsultation = ref(null)
const serviceStarted = ref(false)
const serviceTime = ref(0)
const isInService = ref(false)
const isSessionLocked = ref(false)
const showFinishModal = ref(false)
const isLoadingConsultations = ref(false)
const isLoading = ref(false)
const consultationNotes = ref('')

// Estados para o modal de laudo
const showLaudoModal = ref(false)
const laudoPaciente = ref(null)

// Estados para o modal de atestado
const showAtestadoModal = ref(false)
const atestadoPaciente = ref(null)

// Estados para o modal de prescrição
const showPrescricaoModal = ref(false)
const prescricaoPaciente = ref(null)

// Dados das APIs
const rooms = ref([])
const schedules = ref([])
const consultations = ref([])

// Banner
const banner = ref({ show: false, type: 'success', message: '' })

// Timer para o cronômetro
let serviceTimer = null

// Computed para verificar se o usuário é médico
const userStore = useUserStore()
const isMedico = computed(() => {
  const grupos = userStore.grupos || []
  // Verificar se o usuário pertence a grupos de médicos
  return grupos.some(grupo =>
    grupo.toLowerCase().includes('medico') ||
    grupo.toLowerCase().includes('radiologista') ||
    grupo.toLowerCase().includes('médico') ||
    grupo.toLowerCase().includes('doctor')
  )
})

// Watchers
watch([selectedRoom, selectedSchedule], ([newRoom, newSchedule]) => {
    if (newRoom && newSchedule && !isInService.value) {
        loadConsultations()
    }
})

// Novo watcher para recarregar salas quando a agenda for alterada
watch(selectedSchedule, (newSchedule) => {
    if (newSchedule) {
        loadRoomsForSchedule(newSchedule)
    } else {
        // Se nenhuma agenda estiver selecionada, limpar as salas
        rooms.value = []
        selectedRoom.value = ''
    }
})

// Lifecycle hooks
onMounted(async () => {
    await loadSchedules() // Carregar agendas primeiro
    checkExistingService() // Verificar se há atendimento em andamento
    // Carregar salas após verificar se há atendimento em andamento
    if (selectedSchedule.value) {
        await loadRoomsForSchedule(selectedSchedule.value)
    }
    setupBeforeUnload()
})

onUnmounted(() => {
    if (serviceTimer) {
        clearInterval(serviceTimer)
    }
    window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Métodos de API

async function loadRoomsForSchedule(scheduleId) {
    try {
        // Buscar detalhes da agenda para obter as salas vinculadas
        const response = await apiClient.get(`/api/v1/schedules/${scheduleId}/`)
        const schedule = response.data
        
        // Filtrar apenas salas ativas que estão vinculadas à agenda
        rooms.value = schedule.rooms?.filter(room => room.active) || []
        
        // Se a sala atualmente selecionada não está mais disponível, limpar a seleção
        if (selectedRoom.value && !rooms.value.find(room => room.id === selectedRoom.value)) {
            selectedRoom.value = ''
        }
    } catch (error) {
        console.error('Erro ao carregar salas da agenda:', error)
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
        rooms.value = []
    }
}

async function loadSchedules() {
    try {
        const response = await apiClient.get('/api/v1/schedules/list/')
        schedules.value = response.data.results || response.data
        
        // Se há agendas disponíveis e nenhuma está selecionada, selecionar a primeira
        if (schedules.value.length > 0 && !selectedSchedule.value) {
            selectedSchedule.value = schedules.value[0].id
        }
    } catch (error) {
        console.error('Erro ao carregar agendas:', error)
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
}

async function loadConsultations() {
    if (!selectedRoom.value || !selectedSchedule.value) return
    
    // Verificar se há salas disponíveis para a agenda selecionada
    if (rooms.value.length === 0) {
        consultations.value = []
        return
    }
    
    isLoadingConsultations.value = true
    try {
        const today = new Date().toISOString().split('T')[0]
        const response = await apiClient.get('/api/v1/consultancy/consultations/', {
            params: {
                room_id: selectedRoom.value,
                schedule_id: selectedSchedule.value,
                date: today,
                page_size: 50
            }
        })
        let consultationsData = response.data.results || []
        
        // Ordenar cronologicamente: mais antigos no topo, finalizados no final
        consultationsData.sort((a, b) => {
            // Status finalizados vão para o final
            const completedStatus = ['completed', 'cancelled']
            const aIsCompleted = completedStatus.includes(a.status)
            const bIsCompleted = completedStatus.includes(b.status)
            
            if (aIsCompleted && !bIsCompleted) return 1
            if (!aIsCompleted && bIsCompleted) return -1
            
            // Entre não-finalizados, ordenar sempre por horário de confirmação
            if (!aIsCompleted && !bIsCompleted) {
                const aConfirmed = a.appointment?.confirmed
                const bConfirmed = b.appointment?.confirmed
                
                // Ordenar sempre por horário de confirmação (os segundos nunca serão iguais)
                return new Date(aConfirmed) - new Date(bConfirmed)
            }
            
            // Entre finalizados, manter ordem por data de finalização
            return new Date(a.updated_at) - new Date(b.updated_at)
        })
        
        consultations.value = consultationsData
    } catch (error) {
        console.error('Erro ao carregar consultas:', error)
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
        consultations.value = []
    } finally {
        isLoadingConsultations.value = false
    }
}

async function callPatient(consultation, callType = 'first') {
    if (isLoading.value) return;
    
    isLoading.value = true;
    try {
        const response = await apiClient.post('/api/v1/consultancy/calls/', {
            consultation_id: consultation.id,
            call_type: callType
        });
        
        if (response.status === 201) {
            showBanner('success', `Paciente ${consultation.patient_name} chamado com sucesso!`);
            
            // Atualiza o status da consulta localmente para refletir a mudança imediatamente
            const localConsultation = consultations.value.find(c => c.id === consultation.id);
            if (localConsultation) {
                localConsultation.status = 'called';
                selectedConsultation.value = localConsultation;
            }
        }
    } catch (error) {
        console.error('Erro ao chamar paciente:', error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isLoading.value = false;
    }
}

async function startService(consultation) {
    if (isInService.value) {
        showBanner('error', 'Finalize o atendimento atual antes de iniciar outro.');
        return;
    }

    isLoading.value = true;
    try {
        console.log('🚀 Iniciando atendimento para consulta:', consultation.id);
        console.log('🚀 Status atual da consulta:', consultation.status);
        
        const response = await apiClient.post(`/api/v1/consultancy/consultations/${consultation.id}/start/`);
        
        if (response.status === 200) {
            currentConsultation.value = response.data;
            console.log('✅ Atendimento iniciado com sucesso. Novo status:', currentConsultation.value.status);
            
            serviceStarted.value = true;
            serviceTime.value = 0;
            isInService.value = true;
            
            const serviceData = {
                consultation: currentConsultation.value,
                startTime: Date.now(),
                room: selectedRoom.value,
                schedule: selectedSchedule.value
            };
            localStorage.setItem('currentService', JSON.stringify(serviceData));
            
            startTimer();
            showBanner('success', 'Atendimento iniciado com sucesso!');
            
            await loadConsultations(); // Atualiza a lista com o status 'in_progress'
        }
    } catch (error) {
        console.error('Erro ao iniciar atendimento:', error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro ao iniciar atendimento';
        showBanner('error', errorMessage);
    } finally {
        isLoading.value = false;
    }
}

async function confirmFinishService() {
    if (!currentConsultation.value || isLoading.value) return;
    
    console.log('🔍 Status da consulta antes de finalizar:', currentConsultation.value.status);
    console.log('🔍 Dados da consulta:', currentConsultation.value);
    
    isLoading.value = true;
    try {
        // Salvar observações se houver
        if (consultationNotes.value.trim()) {
            await saveNotes(false) // false = não mostrar notificação
        }
        
        // Verificar se a consulta ainda está em andamento antes de finalizar
        const consultationResponse = await apiClient.get(`/api/v1/consultancy/consultations/${currentConsultation.value.id}/`);
        const currentStatus = consultationResponse.data.status;
        
        console.log('🔍 Status atual da consulta no backend:', currentStatus);
        
        if (currentStatus !== 'in_progress') {
            showBanner('error', `A consulta não está em andamento (status: ${currentStatus}). Não é possível finalizar.`);
            return;
        }
        
        const response = await apiClient.post(`/api/v1/consultancy/consultations/${currentConsultation.value.id}/end/`)
        
        if (response.status === 200) {
            // Marcar paciente como atendido no sistema de agendamentos
            // Isso irá criar automaticamente a conta com os materiais utilizados
            if (currentConsultation.value.appointment) {
                try {
                    await apiClient.post(`/api/v1/schedules/${selectedSchedule.value}/appointments/${currentConsultation.value.appointment.id}/attend`, {
                        attended: true,
                        notes: consultationNotes.value.trim()
                    })
                } catch (error) {
                    console.warn('Erro ao marcar paciente como atendido no sistema de agendamentos:', error)
                    // Não falha o atendimento se houver erro aqui
                }
            }
            
            // Para o timer
            if (serviceTimer) {
                clearInterval(serviceTimer)
                serviceTimer = null
            }
            
            // Limpa estados
            currentConsultation.value = null
            selectedConsultation.value = null
            serviceStarted.value = false
            serviceTime.value = 0
            isInService.value = false
            isSessionLocked.value = false
            showFinishModal.value = false
            consultationNotes.value = ''
            
            // Remove do localStorage
            localStorage.removeItem('currentService')
            
            showBanner('success', 'Atendimento finalizado com sucesso!')
            
            // Recarregar consultas
            await loadConsultations()
        }
    } catch (error) {
        console.error('Erro ao finalizar consulta:', error)
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro ao finalizar atendimento';
        showBanner('error', errorMessage);
    } finally {
        isLoading.value = false
    }
}

async function saveNotes(showNotif = true) {
    if (!currentConsultation.value || !consultationNotes.value.trim() || isLoading.value) return
    
    const wasLoading = isLoading.value
    if (!wasLoading) isLoading.value = true
    
    try {
        const response = await apiClient.put(`/api/v1/consultancy/consultations/${currentConsultation.value.id}/`, {
            notes: consultationNotes.value.trim()
        })
        
        if (response.status === 200) {
            currentConsultation.value.notes = consultationNotes.value.trim()
            if (showNotif) {
                showBanner('success', 'Observações salvas com sucesso!')
            }
        }
    } catch (error) {
        console.error('Erro ao salvar observações:', error)
        if (showNotif) {
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro ao salvar observações';
            showBanner('error', errorMessage);
        }
    } finally {
        if (!wasLoading) isLoading.value = false
    }
}

function updateConsultationNotes(notes) {
    consultationNotes.value = notes
}

// Métodos auxiliares
function checkExistingService() {
    const savedService = localStorage.getItem('currentService')
    if (savedService) {
        try {
            const service = JSON.parse(savedService)
            currentConsultation.value = service.consultation
            selectedRoom.value = service.room
            selectedSchedule.value = service.schedule
            serviceStarted.value = true
            isInService.value = true
            isSessionLocked.value = true
            serviceTime.value = Math.floor((Date.now() - service.startTime) / 1000)
            consultationNotes.value = service.consultation.notes || ''
            startTimer()
            
            // Carregar salas para a agenda do atendimento em andamento
            if (service.schedule) {
                loadRoomsForSchedule(service.schedule)
            }
        } catch (error) {
            console.error('Erro ao recuperar sessão:', error)
            localStorage.removeItem('currentService')
        }
    }
}

function setupBeforeUnload() {
    const handleBeforeUnload = (e) => {
        if (isInService.value) {
            e.preventDefault()
            e.returnValue = 'Você possui um atendimento em andamento. Tem certeza de que deseja sair?'
            return e.returnValue
        }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
}

function selectConsultation(consultation) {
    if (!isInService.value) {
        selectedConsultation.value = consultation
    }
}

function finishService() {
    showFinishModal.value = true;
}

function returnToService() {
    isSessionLocked.value = false;
}

async function openLaudoModal() {
    if (!currentConsultation.value) {
        showBanner('error', 'Nenhuma consulta em andamento para laudar');
        return;
    }
    
    // Verificar se há dados do agendamento
    if (!currentConsultation.value.appointment) {
        showBanner('error', 'Dados do agendamento não disponíveis para laudar');
        return;
    }
    
    // Verificar se há dados do paciente
    if (!currentConsultation.value.appointment.patient?.id) {
        showBanner('error', 'Dados do paciente não disponíveis para laudar');
        console.error('Dados da consulta:', currentConsultation.value);
        return;
    }
    
    // Preparar dados do paciente para o modal de laudo
    laudoPaciente.value = {
        idPaciente: currentConsultation.value.appointment.patient?.id,
        nome: currentConsultation.value.patient_name,
        numAcesso: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        numero: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        exame: currentConsultation.value.appointment.service?.descricao || 'Exame não definido',
        data: currentConsultation.value.appointment.date,
        appointmentId: currentConsultation.value.appointment.id,
        // Dados adicionais que podem ser úteis
        service_id: currentConsultation.value.appointment.service?.id,
        schedule_id: selectedSchedule.value,
        room_id: selectedRoom.value
    };
    
    // Buscar o número da OS (Ordem de Serviço) da conta
    try {
        // Buscar conta pelo appointment_id usando o endpoint de listagem
        const contaResponse = await apiClient.get('/api/v1/faturamento/contas', {
            params: {
                appointment_id: currentConsultation.value.appointment.id,
                page_size: 1
            }
        });
        
        if (contaResponse.data.results && contaResponse.data.results.length > 0) {
            const conta = contaResponse.data.results[0];
            if (conta.numero_os) {
                laudoPaciente.value.numAcesso = conta.numero_os;
                laudoPaciente.value.numero = conta.numero_os;
                console.log('✅ Número da OS encontrado:', conta.numero_os);
            }
        }
    } catch (error) {
        console.log('Conta não encontrada, usando accession_number como fallback');
    }
    
    console.log('🚀 Abrindo modal de laudo para paciente:', laudoPaciente.value);
    showLaudoModal.value = true;
}

function closeLaudoModal() {
    showLaudoModal.value = false;
    laudoPaciente.value = null;
}

function onLaudoSalvo() {
    showBanner('success', 'Laudo salvo com sucesso!');
}

function onLaudoFinalizado() {
    showBanner('success', 'Laudo finalizado com sucesso!');
    // Opcional: fechar o modal após finalizar
    // closeLaudoModal();
}

async function openAtestadoModal() {
    if (!currentConsultation.value) {
        showBanner('error', 'Nenhuma consulta em andamento para emitir atestado');
        return;
    }
    
    // Verificar se há dados do agendamento
    if (!currentConsultation.value.appointment) {
        showBanner('error', 'Dados do agendamento não disponíveis para emitir atestado');
        return;
    }
    
    // Verificar se há dados do paciente
    if (!currentConsultation.value.appointment.patient?.id) {
        showBanner('error', 'Dados do paciente não disponíveis para emitir atestado');
        console.error('Dados da consulta:', currentConsultation.value);
        return;
    }
    
    // Preparar dados do paciente para o modal de atestado
    atestadoPaciente.value = {
        idPaciente: currentConsultation.value.appointment.patient?.id,
        nome: currentConsultation.value.patient_name,
        numAcesso: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        numero: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        exame: currentConsultation.value.appointment.service?.descricao || 'Exame não definido',
        data: currentConsultation.value.appointment.date,
        appointmentId: currentConsultation.value.appointment.id,
        // Dados adicionais que podem ser úteis
        service_id: currentConsultation.value.appointment.service?.id,
        schedule_id: selectedSchedule.value,
        room_id: selectedRoom.value
    };
    
    // Buscar o número da OS (Ordem de Serviço) da conta
    try {
        const contaResponse = await apiClient.get('/api/v1/faturamento/contas', {
            params: {
                appointment_id: currentConsultation.value.appointment.id,
                page_size: 1
            }
        });
        
        if (contaResponse.data.results && contaResponse.data.results.length > 0) {
            const conta = contaResponse.data.results[0];
            if (conta.numero_os) {
                atestadoPaciente.value.numAcesso = conta.numero_os;
                atestadoPaciente.value.numero = conta.numero_os;
                console.log('✅ Número da OS encontrado para atestado:', conta.numero_os);
            }
        }
    } catch (error) {
        console.log('Conta não encontrada para atestado, usando accession_number como fallback');
    }
    
    console.log('🚀 Abrindo modal de atestado para paciente:', atestadoPaciente.value);
    showAtestadoModal.value = true;
}

function closeAtestadoModal() {
    showAtestadoModal.value = false;
    atestadoPaciente.value = null;
}

function onAtestadoEmitido() {
    showBanner('success', 'Atestado emitido com sucesso!');
}

function onAtestadoSalvo() {
    showBanner('success', 'Atestado salvo com sucesso!');
}

async function openPrescricaoModal() {
    if (!currentConsultation.value) {
        showBanner('error', 'Nenhuma consulta em andamento para emitir prescrição');
        return;
    }
    
    // Verificar se há dados do agendamento
    if (!currentConsultation.value.appointment) {
        showBanner('error', 'Dados do agendamento não disponíveis para emitir prescrição');
        return;
    }
    
    // Verificar se há dados do paciente
    if (!currentConsultation.value.appointment.patient?.id) {
        showBanner('error', 'Dados do paciente não disponíveis para emitir prescrição');
        console.error('Dados da consulta:', currentConsultation.value);
        return;
    }
    
    // Preparar dados do paciente para o modal de prescrição
    prescricaoPaciente.value = {
        idPaciente: currentConsultation.value.appointment.patient?.id,
        nome: currentConsultation.value.patient_name,
        numAcesso: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        numero: currentConsultation.value.appointment.accession_number || currentConsultation.value.appointment.id,
        exame: currentConsultation.value.appointment.service?.descricao || 'Exame não definido',
        data: currentConsultation.value.appointment.date,
        appointmentId: currentConsultation.value.appointment.id,
        // Dados adicionais que podem ser úteis
        service_id: currentConsultation.value.appointment.service?.id,
        schedule_id: selectedSchedule.value,
        room_id: selectedRoom.value
    };
    
    // Buscar o número da OS (Ordem de Serviço) da conta
    try {
        const contaResponse = await apiClient.get('/api/v1/faturamento/contas', {
            params: {
                appointment_id: currentConsultation.value.appointment.id,
                page_size: 1
            }
        });
        
        if (contaResponse.data.results && contaResponse.data.results.length > 0) {
            const conta = contaResponse.data.results[0];
            if (conta.numero_os) {
                prescricaoPaciente.value.numAcesso = conta.numero_os;
                prescricaoPaciente.value.numero = conta.numero_os;
                console.log('✅ Número da OS encontrado para prescrição:', conta.numero_os);
            }
        }
    } catch (error) {
        console.log('Conta não encontrada para prescrição, usando accession_number como fallback');
    }
    
    console.log('🚀 Abrindo modal de prescrição para paciente:', prescricaoPaciente.value);
    showPrescricaoModal.value = true;
}

function closePrescricaoModal() {
    showPrescricaoModal.value = false;
    prescricaoPaciente.value = null;
}

function onPrescricaoEmitida() {
    showBanner('success', 'Prescrição emitida com sucesso!');
}

function onPrescricaoSalva() {
    showBanner('success', 'Prescrição salva com sucesso!');
}

function startTimer() {
    if (serviceTimer) clearInterval(serviceTimer)
    
    serviceTimer = setInterval(() => {
        serviceTime.value++
    }, 1000)
}

function showBanner(type, message) {
    // Formata a mensagem para melhor apresentação
    let formattedMessage = message;
    
    // Se a mensagem vier do backend com detalhes, formata melhor
    if (typeof message === 'string' && message.includes('detail')) {
        // Remove prefixos comuns de erro do backend
        formattedMessage = message
            .replace(/^Erro ao .*?: /, '')
            .replace(/^Ocorreu um erro ao .*?: /, '')
            .replace(/^Erro: /, '')
            .replace(/^Erro desconhecido$/, 'Ocorreu um erro inesperado. Tente novamente.');
    }
    
    // Capitaliza a primeira letra se necessário
    if (formattedMessage && formattedMessage.length > 0) {
        formattedMessage = formattedMessage.charAt(0).toUpperCase() + formattedMessage.slice(1);
    }
    
    banner.value = { show: true, type, message: formattedMessage };
}

function getRoomStatus() {
    const room = rooms.value.find(r => r.id === selectedRoom.value)
    return room ? (room.active ? 'Disponível' : 'Inativa') : ''
}

function getRoomStatusColor() {
    const room = rooms.value.find(r => r.id === selectedRoom.value)
    return room?.active ? 'bg-green-500' : 'bg-red-500'
}

function getStatusLabel(status) {
    const labels = {
        'waiting': 'Aguardando',
        'called': 'Chamado',
        'in_progress': 'Em Atendimento',
        'completed': 'Finalizado',
        'cancelled': 'Cancelado'
    };
    return labels[status] || status;
}

function getStatusClass(status) {
    const classes = {
        'waiting': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        'called': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        'in_progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
}

function formatTime(timeString) {
    if (!timeString) return ''
    
    // Se for um número (segundos), formatar como cronômetro
    if (typeof timeString === 'number') {
        const hours = Math.floor(timeString / 3600)
        const minutes = Math.floor((timeString % 3600) / 60)
        const secs = timeString % 60
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    
    // Se for uma string de time, formatar para brasileiro
    if (typeof timeString === 'string') {
        const [hours, minutes] = timeString.split(':')
        return `${hours}:${minutes}`
    }
    
    return ''
}



function formatDateTime(dateTimeString) {
    if (!dateTimeString) return ''
    const date = new Date(dateTimeString)
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR')
}

function handleBeforeUnload(e) {
    if (isInService.value) {
        e.preventDefault()
        e.returnValue = 'Você possui um atendimento em andamento. Tem certeza de que deseja sair?'
        return e.returnValue
    }
}

// Callbacks para eventos do componente MateriaisUtilizados
function onMaterialAdicionado() {
    showBanner('success', 'Material adicionado com sucesso!')
}

function onMaterialRemovido() {
    showBanner('success', 'Material removido com sucesso!')
}

function onMaterialAtualizado() {
    showBanner('success', 'Material atualizado com sucesso!')
}
</script>

<style scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

/* Animações personalizadas */
.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: .5;
    }
}

/* Estilos para scrollbar */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Dark mode scrollbar */
.dark .overflow-y-auto::-webkit-scrollbar-track {
    background: #374151;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #6b7280;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

/* Estilos específicos para o modal de laudo no atendimento */
.laudo-modal-atendimento {
    z-index: 9999;
}

.laudo-modal-atendimento .modal-content {
    max-height: 90vh;
    overflow-y: auto;
}
</style>
